import React, { useState, useEffect } from 'react';
import API from '../../Services/api';
import Header from '../Header';

const TABS = [
  { id: 'overview', label: '📊 Overview' },
  { id: 'users', label: '👥 Users' },
  { id: 'centres', label: '♻️ Recycling Centres' },
  { id: 'requests', label: '📋 Requests' },
  { id: 'feedback', label: '💬 Feedback' },
];

const FEEDBACK_LABELS = {
  bug: { label: 'Bug Report', color: 'bg-red-100 text-red-700' },
  feature: { label: 'Feature Request', color: 'bg-blue-100 text-blue-700' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-700' },
  compliment: { label: 'Compliment', color: 'bg-yellow-100 text-yellow-700' },
};

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [centres, setCentres] = useState([]);
  const [requests, setRequests] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [roleChange, setRoleChange] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = async (tab) => {
    setLoading(true);
    try {
      if (tab === 'users' || tab === 'overview') {
        const res = await API.get('/users/admin/users');
        setUsers(res.data.users || []);
      }
      if (tab === 'centres' || tab === 'overview') {
        const res = await API.get('/collection-points');
        setCentres(res.data || []);
      }
      if (tab === 'requests' || tab === 'overview') {
        const res = await API.get('/service-requests');
        setRequests(res.data || []);
      }
      if (tab === 'feedback' || tab === 'overview') {
        const res = await API.get('/feedback');
        setFeedback(res.data || []);
      }
    } catch (err) {
      console.error('Admin fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  const handleDeleteUser = async (id) => {
    try {
      await API.delete(`/users/admin/users/${id}`);
      setUsers((prev) => prev.filter((u) => u._id !== id));
      setConfirmDelete(null);
      showToast('User deleted successfully.');
    } catch (err) {
      showToast('Failed to delete user.');
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      const res = await API.put(`/users/admin/users/${id}/role`, { role: newRole });
      setUsers((prev) => prev.map((u) => u._id === id ? { ...u, role: res.data.user.role } : u));
      setRoleChange(null);
      showToast(`User role updated to ${newRole}.`);
    } catch (err) {
      showToast('Failed to update role.');
    }
  };

  const handleFeedbackStatus = async (id, status) => {
    try {
      await API.put(`/feedback/${id}/status`, { status });
      setFeedback((prev) => prev.map((f) => f._id === id ? { ...f, status } : f));
      showToast('Feedback status updated.');
    } catch (err) {
      showToast('Failed to update feedback status.');
    }
  };

  const stats = {
    totalUsers: users.filter((u) => u.role === 'user').length,
    totalAdmins: users.filter((u) => u.role === 'admin').length,
    totalCentres: centres.length,
    totalRequests: requests.length,
    pendingRequests: requests.filter((r) => r.status === 'Pending').length,
    newFeedback: feedback.filter((f) => f.status === 'new').length,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFB]">
      <Header isDashboard={true} isAuthenticated={true} />

      {toast && (
        <div className="fixed top-4 right-4 z-[9999] bg-[#29752B] text-white px-5 py-3 rounded-xl shadow-lg text-sm font-medium">
          {toast}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80 shadow-xl text-center space-y-4">
            <p className="text-lg font-bold text-[#172A3A]">Delete User?</p>
            <p className="text-sm text-gray-600">This action cannot be undone. All data for this user will be removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDeleteUser(confirmDelete)} className="flex-1 bg-red-600 text-white py-2 rounded-xl text-sm hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {roleChange && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80 shadow-xl text-center space-y-4">
            <p className="text-lg font-bold text-[#172A3A]">Change Role?</p>
            <p className="text-sm text-gray-600">
              Set <strong>{roleChange.username}</strong> as <strong>{roleChange.newRole}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setRoleChange(null)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleRoleChange(roleChange.id, roleChange.newRole)} className="flex-1 bg-[#29752B] text-white py-2 rounded-xl text-sm hover:bg-[#1e5c20]">Confirm</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#172A3A]">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Manage users, recycling centres, requests and feedback</p>
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#29752B] text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#29752B] hover:text-[#29752B]'
              }`}
            >
              {tab.label}
              {tab.id === 'feedback' && stats.newFeedback > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">{stats.newFeedback}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-[#29752B]" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: 'Total Users', value: stats.totalUsers, icon: '👥', color: 'from-blue-50 to-blue-100' },
                  { label: 'Admins', value: stats.totalAdmins, icon: '🛡️', color: 'from-purple-50 to-purple-100' },
                  { label: 'Recycling Centres', value: stats.totalCentres, icon: '♻️', color: 'from-green-50 to-green-100' },
                  { label: 'Total Requests', value: stats.totalRequests, icon: '📋', color: 'from-yellow-50 to-yellow-100' },
                  { label: 'Pending Requests', value: stats.pendingRequests, icon: '⏳', color: 'from-orange-50 to-orange-100' },
                  { label: 'New Feedback', value: stats.newFeedback, icon: '💬', color: 'from-red-50 to-red-100' },
                ].map((s) => (
                  <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-2xl p-4 border border-white shadow-sm`}>
                    <div className="text-2xl mb-2">{s.icon}</div>
                    <div className="text-2xl font-bold text-[#172A3A]">{s.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-[#172A3A]">All Users ({users.length})</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Name</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Email</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Role</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Joined</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Recycled</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.map((user) => (
                        <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#172A3A]">{user.username}</td>
                          <td className="px-5 py-3 text-gray-600">{user.email}</td>
                          <td className="px-5 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</td>
                          <td className="px-5 py-3 text-gray-600">{user.recyclingHistory?.length || 0} items</td>
                          <td className="px-5 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setRoleChange({ id: user._id, username: user.username, newRole: user.role === 'admin' ? 'user' : 'admin' })}
                                className="text-xs border border-[#29752B] text-[#29752B] px-2 py-1 rounded-lg hover:bg-green-50 transition-colors"
                              >
                                {user.role === 'admin' ? 'Demote' : 'Make Admin'}
                              </button>
                              <button
                                onClick={() => setConfirmDelete(user._id)}
                                className="text-xs border border-red-300 text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && <p className="text-center py-8 text-gray-400">No users found.</p>}
                </div>
              </div>
            )}

            {activeTab === 'centres' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-[#172A3A]">Recycling Centres ({centres.length})</h2>
                  <a href="/collection-points" className="text-sm text-[#29752B] hover:underline font-medium">Manage on Map →</a>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Name</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Address</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Contact</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Items Accepted</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {centres.map((c) => (
                        <tr key={c._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#172A3A]">{c.name}</td>
                          <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{c.address}</td>
                          <td className="px-5 py-3 text-gray-600">{c.collectionPointEmail || c.activeCompanyEmail}</td>
                          <td className="px-5 py-3 text-gray-600">{c.acceptedItems?.length || 0} types</td>
                          <td className="px-5 py-3">
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">Active</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {centres.length === 0 && <p className="text-center py-8 text-gray-400">No recycling centres found.</p>}
                </div>
              </div>
            )}

            {activeTab === 'requests' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-[#172A3A]">Recycling Requests ({requests.length})</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Service requests submitted by users</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Name</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Email</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Devices</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Status</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Submitted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {requests.map((r) => (
                        <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#172A3A]">{r.name}</td>
                          <td className="px-5 py-3 text-gray-600">{r.email}</td>
                          <td className="px-5 py-3 text-gray-600">{(r.deviceTypes || []).join(', ')}</td>
                          <td className="px-5 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              r.status === 'Completed' ? 'bg-green-100 text-green-700' :
                              r.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                              r.status === 'Acknowledged' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {requests.length === 0 && <p className="text-center py-8 text-gray-400">No requests found.</p>}
                </div>
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-[#172A3A]">User Feedback ({feedback.length})</h2>
                  <span className="text-sm text-gray-500">{stats.newFeedback} new</span>
                </div>
                {feedback.length === 0 && <p className="text-center py-8 text-gray-400 bg-white rounded-2xl">No feedback yet.</p>}
                {feedback.map((f) => (
                  <div key={f._id} className={`bg-white rounded-2xl border shadow-sm p-5 ${f.status === 'new' ? 'border-l-4 border-l-[#29752B]' : 'border-gray-100'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${FEEDBACK_LABELS[f.category]?.color || 'bg-gray-100 text-gray-700'}`}>
                            {FEEDBACK_LABELS[f.category]?.label || f.category}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                            f.status === 'new' ? 'bg-green-50 text-green-700 border-green-200' :
                            f.status === 'reviewed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-gray-50 text-gray-500 border-gray-200'
                          }`}>
                            {f.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed">{f.message}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          From: {f.username || 'Unknown'} ({f.email}) · {new Date(f.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {f.status !== 'reviewed' && (
                          <button onClick={() => handleFeedbackStatus(f._id, 'reviewed')} className="text-xs border border-blue-300 text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-50 whitespace-nowrap">Mark Reviewed</button>
                        )}
                        {f.status !== 'resolved' && (
                          <button onClick={() => handleFeedbackStatus(f._id, 'resolved')} className="text-xs border border-green-300 text-green-600 px-2 py-1 rounded-lg hover:bg-green-50 whitespace-nowrap">Resolve</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
